import { credStatus, getToday } from "../utils";

// ═══════════════════════════════════════════════════════════════
// O FIM DO FUNIL — de Documentação até Ativo na Rede
// ═══════════════════════════════════════════════════════════════
//
// Tirar o "avançar estágio" resolveu a mentira do começo do funil (dava para
// marcar "abordagem" sem ter falado com ninguém), mas deixou o fim sem porta:
// empresa que entrava em Documentação não chegava a Ativo por caminho nenhum,
// e o KPI "Ativos na rede" ficou congelado.
//
// A regra aqui é a mesma do começo: o estágio é consequência de um FATO.
// A diferença é de quem é o fato.
//
//   Documentação → Validação   AUTOMÁTICO quando a ficha está ASSINADA — a
//                              Etapa 1, o documento de QUEM assina. Os
//                              complementares (contrato social, doc do
//                              representante) são a Etapa 2: cobrados depois
//                              pelo link e enviados ao módulo como
//                              docs_faltantes. NÃO seguram o cadastro — é a
//                              mesma regra que o vínculo com o módulo já usa.
//                              Prender a ficha assinada esperando esses papéis
//                              era o oposto do que o resto do sistema faz.
//
//   Validação → Ativo          AUTOMÁTICO quando o fornecedor EXISTE no módulo
//                              (provider_id). Não espera aprovação da gerência:
//                              o credenciamento é a assinatura eletrônica + o
//                              cadastro no módulo, e o documento é coletado
//                              DEPOIS, dentro de cada módulo, quando o
//                              fornecedor efetivamente fatura. "Validação"
//                              passou a significar "assinada, aguardando o
//                              vínculo com o módulo" — não uma fila de conferência.
//
//   Ativação → Ativo           AUTOMÁTICO idem (provider_id). O gerente ainda
//                              tem os atos manuais (reprovar, ou ativar à mão
//                              quando não há integração), mas deixaram de ser o
//                              caminho: vinculou, ativou.
//
// Só anda para frente. Documento removido depois não devolve a empresa para
// trás: o passado dela é a trilha, não o estado de hoje.

export const ETAPAS_FIM = ["documentacao", "validacao", "ativacao", "ativo"];

/** O que se sabe da ficha, sem opinião: o que está lá e o que falta. */
export function evidencia(ficha) {
  if (!ficha) return { temFicha: false, assinada: false, docsPendentes: [], completa: false, provedores: [] };
  const st = credStatus(ficha);
  const assinada = ficha.status === "concluido";
  const docsPendentes = st.docsPend || [];
  const sync = ficha.integracaoSync && typeof ficha.integracaoSync === "object" ? ficha.integracaoSync : {};
  const provedores = Object.entries(sync)
    .filter(([, v]) => v && typeof v === "object" && (v.provider_id || v.providerId))
    .map(([sistema, v]) => ({ sistema, providerId: v.provider_id || v.providerId }));
  return {
    temFicha: true,
    assinada,
    docsPendentes,
    completa: assinada && docsPendentes.length === 0,
    provedores,
  };
}

/**
 * O avanço que os fatos já autorizam, sem ninguém clicar. Devolve null quando
 * não há nada a fazer — o que é o caso na maioria das empresas, na maior parte
 * do tempo.
 */
export function avancoAutomatico(parceiro, ficha) {
  if (!parceiro || !ficha) return null;
  const e = evidencia(ficha);

  // Assinada já destrava. Os complementares que faltam viajam na nota (e vão
  // para o módulo como docs_faltantes), mas não seguram o avanço — Etapa 2 é
  // cobrança, não trava.
  // Antes de Documentação (identificado/abordagem) também anda, mas só com o
  // ACEITE do fornecedor registrado na ficha: a regra de 13/08 ("não pula
  // abordagem por causa de ficha") é de quando a ficha só chegava depois do
  // "Aceitou" do comercial. Com a ficha do link chegando já assinada, o card
  // ficava em Abordagem para sempre (SULTIRE, Eletro Motores, No Auto Peças —
  // parados desde ago/set). O aceite assinado é prova mais forte que o clique.
  const aceiteNaFicha = !!ficha.aceiteTermos || !!ficha.assinaturaPath;
  const antesDaDoc = ["identificado", "abordagem"].includes(parceiro.stage);
  if ((parceiro.stage === "documentacao" || (antesDaDoc && aceiteNaFicha)) && e.assinada) {
    const faltam = e.docsPendentes.length
      ? ` Faltam ${e.docsPendentes.map((d) => d.label).join(" e ")} — cobrados pelo link depois, não travam o cadastro.`
      : "";
    return {
      para: "validacao",
      nota: `Ficha assinada — pronta para o gerente conferir e cadastrar.${faltam}`,
      evidencia: e.completa ? "ficha_completa" : "ficha_assinada",
    };
  }

  // Fornecedor no módulo (provider_id) = operando. Vale para validação E
  // ativação: assim que o vínculo volta, a empresa fica Ativa sem depender de a
  // gerência aprovar. O documento não trava — é coletado depois, no módulo, no
  // faturamento.
  if (["validacao", "ativacao"].includes(parceiro.stage) && e.provedores.length > 0) {
    const onde = e.provedores.map((p) => `${p.sistema} #${p.providerId}`).join(", ");
    return {
      para: "ativo",
      nota: `Fornecedor no sistema (${onde}) — operando na rede.`,
      evidencia: "provider_id",
    };
  }

  return null;
}

/** Todos os avanços pendentes na base. Roda no carregamento, uma vez. */
export function avancosPendentes(partners = [], porParceiro = () => null) {
  const lista = [];
  for (const p of partners) {
    const mov = avancoAutomatico(p, porParceiro(p));
    if (mov) lista.push({ id: p.id, ...mov });
  }
  return lista;
}

/** Aplica o avanço, deixando na trilha QUAL fato o causou. */
export function aplicarAvanco(parceiro, mov, hoje = getToday()) {
  return {
    ...parceiro,
    stage: mov.para,
    stageHistory: [
      ...(Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : []),
      {
        from: parceiro.stage, to: mov.para, date: hoje,
        // Autor "sistema" de propósito: ninguém decidiu isso, o fato decidiu.
        // Assinar com o nome de quem estava com a tela aberta seria falso.
        autor: "sistema", tipo: "avanco_automatico", evidencia: mov.evidencia,
        note: mov.nota,
      },
    ],
  };
}

// ── Os atos do gerente ───────────────────────────────────────────
//
// O comercial executa até o aceite e cuida da documentação. Daqui para a
// frente quem decide é a gerência — e por isso estas ações não aparecem para
// o perfil comercial.

export const ATOS_GERENTE = {
  // Validação NÃO tem mais "aprovar": a empresa vira Ativa sozinha quando o
  // fornecedor existe no módulo (provider_id). Sobra só o "reprovar", para
  // barrar um fornecedor que não deve entrar — o resto é consequência do vínculo.
  validacao: [
    { id: "reprovar", label: "Reprovar", para: "recusado", cor: "#dc2626", precisaNota: true,
      nota: "Reprovado na validação" },
  ],
  ativacao: [
    { id: "ativar", label: "Ativar na rede", para: "ativo", cor: "#059669", precisaNota: false,
      nota: "Ativado na rede pela gerência (confirmação manual — o sistema ainda não devolveu o cadastro do fornecedor)." },
    { id: "reprovar", label: "Reprovar", para: "recusado", cor: "#dc2626", precisaNota: true,
      nota: "Reprovado na ativação" },
  ],
};

export const atosDoGerente = (estagio) => ATOS_GERENTE[estagio] || [];

export function aplicarAtoGerente(parceiro, ato, { motivo = "", autor = "" } = {}, hoje = getToday()) {
  const texto = ato.precisaNota ? `${ato.nota}: ${motivo.trim()}` : ato.nota;
  return {
    ...parceiro,
    stage: ato.para,
    ...(ato.para === "recusado" ? { nextAction: "", nextActionDate: "" } : {}),
    stageHistory: [
      ...(Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : []),
      {
        from: parceiro.stage, to: ato.para, date: hoje, autor,
        tipo: ato.para === "recusado" ? "recusado" : "ato_gerente",
        motivo: motivo.trim(), note: texto,
      },
    ],
  };
}
